import { AfterViewInit, Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { ApiService } from 'app/services/api.service';
import { BehaviorSubject, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

export interface IEverWatchForm {
  everWatchId: string;
  lyraId: string;
  locationId: string;
  deviceName: string;
}

@Component({
    selector: 'app-everwatch-form',
    imports: [CommonModule, TranslateModule, ReactiveFormsModule, MatTooltipModule, MatFormFieldModule, MatSelectModule, MatOptionModule],
    templateUrl: './everwatch-form.component.html',
    changeDetection: ChangeDetectionStrategy.Default,
    styleUrls: ['./everwatch-form.component.css']
})
export class EverwatchFormComponent implements AfterViewInit {
  @Output() savedEvent = new EventEmitter<string>();

  @Input() locations: { id: string, name: string }[];
  @Input() errorMsg$: BehaviorSubject<string>;

  form: FormGroup;
  showSpinner: boolean = false;

  constructor(private api: ApiService) {
    this.form = new FormGroup({
      everWatchId: new FormControl({ value: '', disabled: false }, Validators.required),
      lyraId: new FormControl({ value: '', disabled: true }, Validators.required),
      locationId: new FormControl('', Validators.required),
      deviceName: new FormControl(''),
    });
  }

  verifyId(): void {
    this.showSpinner = true;
    this.errorMsg$?.next("");

    this.api.getEverWatchAvailability(this.form.controls.everWatchId.value).subscribe({
      next: res => {
        if (!res) {
          this.errorMsg$?.next('ERROR');
          console.error('API error: no response received');
        }
        else if (!res.available) {
          this.errorMsg$?.next('DEVICE_DONT_EXIST_OR_ALREADY_IN_USE');
        }
        else if (!res.lyraId) {
          this.errorMsg$?.next('ERROR');
          console.error('API error: Lyra ID was not provided');
        }
        else {
          this.form.controls.lyraId.setValue(res.lyraId);
          this.form.controls.everWatchId.disable();
        }

        this.showSpinner = false;
      },
      error: err => {
        console.error(err.message);
        this.errorMsg$?.next("ERROR");
        this.showSpinner = false;
      }
    })
  }

  submit(): IEverWatchForm {
    if (this.form.invalid) return null;

    const request: IEverWatchForm = {
      everWatchId: this.form.controls.everWatchId.value,
      lyraId: this.form.controls.lyraId.value,
      locationId: this.form.controls.locationId.value,
      deviceName: this.form.controls.deviceName.value
    }

    this.showSpinner = true;

    this.api.addEverWatch(request).subscribe({
      next: () => {
        this.showSpinner = false;
        this.savedEvent.emit(request.lyraId);

        this.getCurrentFeatures().subscribe(features => {
          if (!features.includes("Everwatch")) {
            const updatedFeatures = [...features, "Everwatch"];
            this.api.submitFeatures(updatedFeatures).subscribe({
              next: () => {
                localStorage.setItem("features", JSON.stringify(updatedFeatures));
              },
              error: err => {
                console.error(err);
              }
            });
          }

          if (features.includes("DeviceRegistry")) {
            const dev = {Id: request.lyraId, DevClass: 1, Name: request.deviceName};
            this.api.addDeviceToInventory(dev).subscribe(() => {
            }, (err) => {
              console.error(err);
            });
          }
        });
      },
      error: err => {
        console.error(err.message);
        this.errorMsg$?.next("ERROR");
        this.showSpinner = false;
      }
    });

    return request;
  }

  clearForm(): void {
    this.form.reset();
    this.form.controls.locationId.setValue('');
    this.form.controls.everWatchId.enable();
  }

  ngAfterViewInit(): void {
    // Use a timeout to avoid NG0100: ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => this.errorMsg$?.next(""));
  }

  private getCurrentFeatures() {
    const featuresRaw = localStorage.getItem("features");

    if (featuresRaw != null) {
      try {
        const features = JSON.parse(featuresRaw);
        if (Array.isArray(features) && features.length > 0) {
          return of(features);
        }
      } catch {
        // Fallback to API if local storage value is invalid JSON.
      }
    }

    return this.api.getFeatures(true).pipe(
      map((response: string[]) => Array.isArray(response) ? response : []),
      catchError(err => {
        console.error(err);
        return of([]);
      })
    );
  }
}
